import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { fetchAllOrders } from "../../redux/slices/adminOrderSlice";
import { fetchAdminProducts } from "../../redux/slices/adminProductSlice";
import { getAllVendors } from "../../redux/slices/vendorSlice";
import { BadgeIndianRupee, Package, Shirt, Store, Users, Clock, TrendingUp } from "lucide-react";

const AdminHome = () => {
  const dispatch = useDispatch();
  const { products, loading: productsLoading, error: productsError } = useSelector((state) => state.adminProducts);
  const { orders, totalOrders, totalSales, loading: ordersLoading, error: ordersError } = useSelector((state) => state.adminOrders);
  const { vendors, loading: vendorsLoading } = useSelector((state) => state.vendor);

  useEffect(() => {
    dispatch(fetchAdminProducts());
    dispatch(fetchAllOrders());
    dispatch(getAllVendors({ status: 'all' }));
  }, [dispatch]);

  // Calculate vendor statistics
  const vendorStats = {
    total: vendors?.length || 0,
    pending: vendors?.filter(v => v.status === 'pending').length || 0,
    active: vendors?.filter(v => v.status === 'approved').length || 0,
    totalRevenue: vendors?.reduce((sum, v) => sum + (v.stats?.totalRevenue || 0), 0) || 0,
    totalProducts: vendors?.reduce((sum, v) => sum + (v.stats?.totalProducts || 0), 0) || 0,
  };

  // Calculate platform vs vendor revenue
  const platformRevenue = totalSales - vendorStats.totalRevenue;
  const vendorRevenue = vendorStats.totalRevenue;

  return (
    <div className="max-w-7xl mx-auto p-6">
      <h1 className="text-3xl font-bold mb-6">Admin Dashboard</h1>
      
      {productsLoading || ordersLoading || vendorsLoading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-black"></div>
        </div>
      ) : productsError ? (
        <p className="text-red-500">Error Fetching Products: {productsError}</p>
      ) : ordersError ? (
        <p className="text-red-500">Error Fetching Orders: {ordersError}</p>
      ) : (
        <>
          {/* Main Stats Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            {/* Total Revenue */}
            <div className="p-6 shadow-md rounded-lg bg-gradient-to-br from-green-500 to-green-600 text-white">
              <h2 className="text-lg font-semibold mb-2">Total Revenue</h2>
              <div className="flex justify-between items-center">
                <p className="text-3xl font-bold">₹{totalSales.toFixed(2)}</p>
                <BadgeIndianRupee className="w-10 h-10 opacity-80" />
              </div>
              <p className="text-sm mt-2 opacity-90">Platform + Vendors</p>
            </div>

            {/* Total Orders */}
            <div className="p-6 shadow-md rounded-lg bg-gradient-to-br from-blue-500 to-blue-600 text-white">
              <h2 className="text-lg font-semibold mb-2">Total Orders</h2>
              <div className="flex justify-between items-center">
                <p className="text-3xl font-bold">{totalOrders}</p>
                <Package className="w-10 h-10 opacity-80" />
              </div>
              <Link to="/admin/orders" className="text-sm mt-2 underline opacity-90 hover:opacity-100">
                Manage Orders →
              </Link>
            </div>

            {/* Total Products */}
            <div className="p-6 shadow-md rounded-lg bg-gradient-to-br from-purple-500 to-purple-600 text-white">
              <h2 className="text-lg font-semibold mb-2">Total Products</h2>
              <div className="flex justify-between items-center">
                <p className="text-3xl font-bold">{products.length}</p>
                <Shirt className="w-10 h-10 opacity-80" />
              </div>
              <Link to="/admin/products" className="text-sm mt-2 underline opacity-90 hover:opacity-100">
                Manage Products →
              </Link>
            </div>

            {/* Total Vendors */}
            <div className="p-6 shadow-md rounded-lg bg-gradient-to-br from-orange-500 to-orange-600 text-white">
              <h2 className="text-lg font-semibold mb-2">Active Vendors</h2>
              <div className="flex justify-between items-center">
                <p className="text-3xl font-bold">{vendorStats.active}</p>
                <Store className="w-10 h-10 opacity-80" />
              </div>
              <Link to="/admin/vendors" className="text-sm mt-2 underline opacity-90 hover:opacity-100">
                Manage Vendors →
              </Link>
            </div>
          </div>

          {/* Vendor Overview Section */}
          <div className="mb-8">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-2xl font-bold">Marketplace Overview</h2>
              <Link 
                to="/admin/vendors" 
                className="text-blue-600 hover:underline font-medium"
              >
                View All Vendors →
              </Link>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {/* Pending Approvals */}
              {vendorStats.pending > 0 && (
                <div className="bg-yellow-50 border-l-4 border-yellow-500 p-6 rounded-lg">
                  <div className="flex items-center gap-3 mb-2">
                    <Clock className="w-6 h-6 text-yellow-600" />
                    <h3 className="font-bold text-lg text-yellow-900">Pending Approvals</h3>
                  </div>
                  <p className="text-3xl font-bold text-yellow-700 mb-2">{vendorStats.pending}</p>
                  <p className="text-sm text-yellow-700">Vendor applications awaiting review</p>
                  <Link 
                    to="/admin/vendors?status=pending" 
                    className="text-yellow-800 font-medium underline mt-2 inline-block"
                  >
                    Review Now →
                  </Link>
                </div>
              )}

              {/* Vendor Revenue */}
              <div className="bg-white border p-6 rounded-lg shadow-sm">
                <div className="flex items-center gap-3 mb-2">
                  <TrendingUp className="w-6 h-6 text-green-600" />
                  <h3 className="font-bold text-lg">Vendor Revenue</h3>
                </div>
                <p className="text-3xl font-bold text-green-600 mb-2">
                  ₹{vendorStats.totalRevenue.toFixed(2)}
                </p>
                <p className="text-sm text-gray-600">{vendorStats.total} total vendors</p>
              </div>

              {/* Vendor Products */}
              <div className="bg-white border p-6 rounded-lg shadow-sm">
                <div className="flex items-center gap-3 mb-2">
                  <Package className="w-6 h-6 text-purple-600" />
                  <h3 className="font-bold text-lg">Vendor Products</h3>
                </div>
                <p className="text-3xl font-bold text-purple-600 mb-2">
                  {vendorStats.totalProducts}
                </p>
                <p className="text-sm text-gray-600">Listed by {vendorStats.active} vendors</p>
              </div>
            </div>
          </div>

          {/* Revenue Breakdown */}
          <div className="mb-8 bg-white p-6 rounded-lg shadow-md">
            <h2 className="text-xl font-bold mb-4">Revenue Breakdown</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <div className="flex justify-between items-center mb-2">
                  <span className="text-gray-600">Platform Revenue</span>
                  <span className="font-bold">₹{platformRevenue.toFixed(2)}</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-3">
                  <div 
                    className="bg-blue-600 h-3 rounded-full" 
                    style={{ width: `${(platformRevenue / totalSales) * 100}%` }}
                  ></div>
                </div>
              </div>
              
              <div>
                <div className="flex justify-between items-center mb-2">
                  <span className="text-gray-600">Vendor Revenue</span>
                  <span className="font-bold">₹{vendorRevenue.toFixed(2)}</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-3">
                  <div 
                    className="bg-green-600 h-3 rounded-full" 
                    style={{ width: `${(vendorRevenue / totalSales) * 100}%` }}
                  ></div>
                </div>
              </div>
            </div>
          </div>

          {/* Recent Orders Table */}
          <div className="bg-white rounded-lg shadow-md">
            <div className="p-6 border-b">
              <h2 className="text-2xl font-bold">Recent Orders</h2>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full text-left text-gray-500">
                <thead className="bg-gray-100 text-xs uppercase text-gray-700">
                  <tr>
                    <th className="py-3 px-4">Order ID</th>
                    <th className="py-3 px-4">User</th>
                    <th className="py-3 px-4">Total Price</th>
                    <th className="py-3 px-4">Type</th>
                    <th className="py-3 px-4">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {orders.length > 0 ? (
                    orders.slice(0, 10).map((order) => (
                      <tr key={order._id} className="border-b hover:bg-gray-50 cursor-pointer">
                        <td className="p-4 font-mono text-xs">
                          {order._id.slice(-8).toUpperCase()}
                        </td>
                        <td className="p-4">{order.user?.name || 'Unknown User'}</td>
                        <td className="p-4 font-semibold">₹{order.totalPrice}</td>
                        <td className="p-4">
                          {order.isMultiVendor ? (
                            <span className="px-2 py-1 bg-purple-100 text-purple-700 text-xs rounded">
                              Multi-Vendor
                            </span>
                          ) : order.vendorBreakdown?.length > 0 ? (
                            <span className="px-2 py-1 bg-green-100 text-green-700 text-xs rounded">
                              Vendor
                            </span>
                          ) : (
                            <span className="px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded">
                              Platform
                            </span>
                          )}
                        </td>
                        <td className="p-4">
                          <span className={`px-2 py-1 text-xs rounded ${
                            order.status === 'Delivered' ? 'bg-green-100 text-green-700' :
                            order.status === 'Shipped' ? 'bg-blue-100 text-blue-700' :
                            order.status === 'Cancelled' ? 'bg-red-100 text-red-700' :
                            'bg-yellow-100 text-yellow-700'
                          }`}>
                            {order.status}
                          </span>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={5} className="p-4 text-center text-gray-500">
                        No Recent Orders Found.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default AdminHome;
